import dayjs from "dayjs";
import * as React from "react";
import { useLocation, useParams } from "react-router-dom";
import { CloudService, IBuildDto } from "../../../../services/cloud-service";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { withManagement } from "../../../../shared/hoc/with-management";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaDatePicker from "../../../ui-kit/kuika-date-picker/kuika-date-picker";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTable, { KuikaTableColumn } from "../../../ui-kit/kuika-table/kuika-table";
import KuikaH4 from "../../../ui-kit/typography/H4/H4";
import KuikaTextMedium from "../../../ui-kit/typography/TextMedium/TextMedium";
import "./deployment-details.scss";
import "./deployment.scss";

type DeploymentDetailRow = {
  id: string;
  buildNumber: number;
  environment: string;
  status: string;
  deployer: string;
  deployedAt: string;
  result: string;
  deploymentChannel?: string;
};

interface DeploymentDetailsProps extends IWithHistoryProp {
  appName?: string;
}

interface DeploymentDetailsState {
  // The kit has no range picker, so the antd `RangePicker`'s dayjs tuple became two
  // independent `KuikaDatePicker`s. Each holds the picker's own raw `YYYY-MM-DD` string
  // ("" when unset); the filter still parses them with dayjs and, exactly as before, only
  // narrows once BOTH ends are set.
  startDate: string;
  endDate: string;
  env: string | number | null;
  status: string | number | null;
  builds: IBuildDto[];
  loading: boolean;
  error?: string;
}

class DeploymentDetailsBase extends React.Component<DeploymentDetailsProps, DeploymentDetailsState> {
  constructor(props: DeploymentDetailsProps) {
    super(props);
    this.state = {
      startDate: "",
      endDate: "",
      env: null,
      status: null,
      builds: [],
      loading: false,
      error: undefined
    };
  }

  componentDidMount() {
    const { appName } = this.props;
    if (appName) {
      this.fetchBuildsForProject(appName);
    }
  }

  componentDidUpdate(prevProps: Readonly<DeploymentDetailsProps>) {
    if (prevProps.appName !== this.props.appName && this.props.appName) {
      this.fetchBuildsForProject(this.props.appName);
    }
  }

  fetchBuildsForProject = async (projectName: string) => {
    this.setState({ loading: true, error: undefined });

    try {
      const response = await CloudService.GetBuilds(projectName, 1000);

      if (response && response.data) {
        // Sort builds by number descending to show latest first
        const sortedBuilds = response.data.sort((a, b) => b.number - a.number);
        this.setState({ builds: sortedBuilds, loading: false });
      } else {
        throw new Error("No data received from cloud service");
      }
    } catch (error) {
      console.error("Error fetching builds for project:", error);
      this.setState({
        error: error instanceof Error ? error.message : "Failed to fetch builds",
        loading: false
      });
    }
  };

  // Two independent pickers can be put in an order a range picker made impossible, and an
  // end before a start silently matches nothing. Each end pushes the other ahead of it
  // rather than leaving the filter in a state that can only return zero rows. The strings
  // are `YYYY-MM-DD`, so comparing them as text orders them by date.
  handleStartDateChange = (next: string | null): void => {
    const startDate: string = next ?? "";
    const shouldPushEnd: boolean =
      startDate.length > 0 && this.state.endDate.length > 0 && startDate > this.state.endDate;
    this.setState({ startDate, endDate: shouldPushEnd ? startDate : this.state.endDate });
  };

  handleEndDateChange = (next: string | null): void => {
    const endDate: string = next ?? "";
    const shouldPullStart: boolean =
      endDate.length > 0 && this.state.startDate.length > 0 && endDate < this.state.startDate;
    this.setState({ endDate, startDate: shouldPullStart ? endDate : this.state.startDate });
  };

  handleEnvChange = (v: any) => {
    this.setState({ env: v });
  };

  handleStatusChange = (v: any) => {
    this.setState({ status: v });
  };

  getBuildStatusText = (result: string): string => {
    switch (result.toUpperCase()) {
      case "SUCCESS":
      case "STABLE":
        return "Ready";
      case "FAILURE":
      case "FAILED":
        return "Failed";
      case "UNSTABLE":
        return "Unstable";
      case "IN_PROGRESS":
      case "BUILDING":
        return "Building";
      default:
        return "Unknown";
    }
  };

  getBuildStatusColor = (result: string): string => {
    switch (result.toUpperCase()) {
      case "SUCCESS":
      case "STABLE":
        return "var(--green-6)";
      case "FAILURE":
      case "FAILED":
        return "var(--red-6)";
      case "UNSTABLE":
        return "var(--yellow-6)";
      case "IN_PROGRESS":
      case "BUILDING":
        return "var(--blue-6)";
      default:
        return "var(--dark-4)";
    }
  };

  private getStatusVariant = (status: string): "ready" | "failed" | "building" | "unstable" | "unknown" => {
    switch ((status || "").toLowerCase()) {
      case "ready":
        return "ready";
      case "failed":
        return "failed";
      case "building":
        return "building";
      case "unstable":
        return "unstable";
      default:
        return "unknown";
    }
  };

  getFilteredData = (): DeploymentDetailRow[] => {
    const { builds, startDate, endDate, env, status } = this.state;

    return builds
      .filter((build) => {
        // Filter by date range — day-granularity comparison, unchanged: the pickers emit
        // `YYYY-MM-DD`, which dayjs parses as ISO, so the bounds land on the same days the
        // RangePicker's dayjs objects did.
        if (startDate.length > 0 && endDate.length > 0) {
          const buildDate = dayjs(build.date);
          if (buildDate.isBefore(dayjs(startDate), "day") || buildDate.isAfter(dayjs(endDate), "day")) {
            return false;
          }
        }

        // Filter by environment/channel
        if (env && env !== "all") {
          const channel = (build.deploymentChannel || "web").toLowerCase();
          if (channel !== String(env).toLowerCase()) {
            return false;
          }
        }

        // Filter by status
        if (status && status !== "all") {
          const buildStatus = this.getBuildStatusText(build.result);
          if (buildStatus.toLowerCase() !== String(status).toLowerCase()) {
            return false;
          }
        }

        return true;
      })
      .map((build, index) => ({
        id: `build-${build.number}-${index}`,
        buildNumber: build.number,
        environment: build.deploymentChannel ? build.deploymentChannel.toUpperCase() : "WEB",
        status: this.getBuildStatusText(build.result),
        deployer: "by",
        deployedAt: build.date,
        result: build.result,
        deploymentChannel: build.deploymentChannel
      }));
  };

  render() {
    const { navigator, appName = "" } = this.props;
    const { startDate, endDate, env, status, loading, error } = this.state;

    const columns: KuikaTableColumn<DeploymentDetailRow>[] = [
      {
        id: "buildNumber",
        accessorKey: "buildNumber",
        enableSorting: true,
        width: 120,
        header: () => "Build",
        cell: (row) => (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "var(--radius-pill)",
                backgroundColor: this.getBuildStatusColor(row.result)
              }}
            />
            <KuikaTextMedium>#{row.buildNumber}</KuikaTextMedium>
          </div>
        )
      },
      {
        id: "environment",
        accessorKey: "environment",
        enableSorting: true,
        width: 120,
        header: () => "Channel",
        cell: (row) => (row.environment ? row.environment.charAt(0) + row.environment.slice(1).toLowerCase() : "")
      },
      {
        id: "status",
        accessorKey: "status",
        enableSorting: true,
        width: 120,
        header: () => "Status",
        cell: (row) => {
          const variant = this.getStatusVariant(row.status);
          return (
            <div className="deployment__status" aria-label={`Deployment status: ${row.status}`}>
              <div className={`deployment__status-badge deployment__status-badge--${variant}`}>
                <span className="deployment__status-dot" aria-hidden="true" />
                <span className="deployment__status-label">{row.status}</span>
              </div>
            </div>
          );
        }
      },
      {
        id: "deployedAt",
        accessorKey: "deployedAt",
        enableSorting: true,
        width: 180,
        header: () => "Deployed At",
        cell: (row) => {
          try {
            return dayjs(row.deployedAt).format("DD.MM.YYYY - HH:mm:ss");
          } catch {
            return row.deployedAt;
          }
        }
      },
      {
        id: "actions",
        enableSorting: false,
        header: () => <></>,
        align: "right",
        width: 32,
        cell: () => (
          <button className="deployment-details__options" aria-label="More options">
            <KuikaSvg name="dots" width={16} height={16} pointer />
          </button>
        )
      }
    ];

    const filtered = this.getFilteredData();

    const handleRowClick = (row: DeploymentDetailRow) => {
      navigator.navigate(
        `/${navigator.getCurrentDomain()}/cloud/deployments/${encodeURIComponent(appName)}/production/${
          row.buildNumber
        }${row.deploymentChannel ? `?channel=${encodeURIComponent(row.deploymentChannel)}` : ""}`
      );
    };

    return (
      <div className="kuika-settings-panel deployment-details">
        <div className="kuika-settings-panel__header">
          <div className="kuika-settings-panel__header-left">
            <KuikaSvg width={24} height={24} name="rocket" />
            <KuikaH4>Deployments / {appName}</KuikaH4>
          </div>
          <div className="kuika-settings-panel__header-right deployment-details__filters" aria-label="Filters">
            <div className="deployment-details__date-filter">
              <KuikaDatePicker
                mode="date"
                value={startDate}
                placeholder="Start Date"
                className="deployment-details__date-filter-picker"
                showClear
                onChange={this.handleStartDateChange}
              />
              <KuikaDatePicker
                mode="date"
                value={endDate}
                placeholder="End Date"
                className="deployment-details__date-filter-picker"
                showClear
                onChange={this.handleEndDateChange}
              />
            </div>

            <KuikaSelectbox
              placeholder="All channels"
              value={env as any}
              options={[
                { value: "all", label: "All" },
                { value: "web", label: "Web" },
                { value: "mobile", label: "Mobile" }
              ]}
              onChange={this.handleEnvChange}
            />

            <KuikaSelectbox
              placeholder="Status"
              value={status as any}
              options={[
                { value: "all", label: "All" },
                { value: "ready", label: "Ready" },
                { value: "building", label: "Building" },
                { value: "failed", label: "Failed" },
                { value: "unstable", label: "Unstable" }
              ]}
              onChange={this.handleStatusChange}
            />
          </div>
        </div>

        <div className="deployment-details__content">
          {error && (
            <div className="deployment-details__error" style={{ padding: "16px", color: "var(--red-6)" }}>
              Error: {error}
              <KuikaButton
                type="secondary"
                size="sm"
                onClick={() => appName && this.fetchBuildsForProject(appName)}
                style={{ marginLeft: "8px" }}
              >
                Retry
              </KuikaButton>
            </div>
          )}
          {!error && (
            <KuikaTable<DeploymentDetailRow>
              data={filtered}
              columns={columns}
              rowKey={(row) => row.id}
              showCountLabel="Deployments"
              enableSorting
              enablePagination
              loading={loading}
              onRowClick={handleRowClick}
              emptyIcon={<KuikaSvg name="rocket" width={48} height={48} style={{ opacity: 0.5 }} />}
              emptyTitle="No deployments found"
              emptyDescription="This project has no deployment history"
            />
          )}
        </div>
      </div>
    );
  }
}

const DeploymentDetailsWithParams = (props: IWithHistoryProp) => {
  const { appName } = useParams<{ appName: string }>();
  const location = useLocation();
  return <DeploymentDetailsBase {...props} appName={appName} />;
};

const DeploymentDetails = withManagement(withHistory(DeploymentDetailsWithParams));
export { DeploymentDetails };
